import { Badge, type BadgeTone, Tooltip } from '@pms-digital/design-system';
import type { ReactNode } from 'react';

/**
 * The state pill of the interview screens (issue #11, §12 of the chat spec).
 *
 * ## It came from `admin/journal-cells.tsx`, and it moved rather than being
 * copied
 *
 * The sessions table used to own it, and the interviews borrowed it so a state
 * would read the same way on both screens. The coupe (issue #32) deletes the
 * sessions table - the page is rewritten by a later lot (§12) - and two of the
 * three cells in that file went with it. Leaving one export behind under a name
 * that describes a screen the app no longer has would have been the worse half
 * of both options, so the badge follows its only remaining caller.
 *
 * Drawn by the package's `Badge` since the design-system migration: the six
 * tones are the package's own, so a state reads the same here as in every PMS
 * application. The palette stays at level 2 - black, white and bleu-nuit -
 * except where a state MEANS something: vert for a conversation that ended,
 * bleu-ciel for one under way, jaune for one abandoned, rouge nowhere here
 * (nothing in this reading is an irreversible action).
 */

/** The six tones a pill may wear: the package's own. */
export type { BadgeTone };

/**
 * The size of the mark's text: 12px by default, and the 13 and 14px of the screens aligned on
 * the design system (#216, npc.md §26.7) - the block « Détermination du noyau » and the replay tables.
 *
 * The package's `Badge` has one size, 12px: the two larger ones keep the pill of #216 on the
 * charte's tokens, in the package's own tone colours, until the package offers a size.
 */
export type BadgeSize = 'xs' | 'caption' | 'sm';

const SIZE_CLASS: Record<Exclude<BadgeSize, 'xs'>, string> = {
  caption: 'text-caption',
  sm: 'text-small',
};

/** The package's `Badge` palette, so a tone reads the same at every size. */
const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: 'bg-gray-100 text-gray-800',
  info: 'bg-info-tint text-info-deep',
  success: 'bg-success-tint text-success-deep',
  warning: 'bg-warning-tint text-warning',
  danger: 'bg-danger-tint text-danger-deep',
  accent: 'bg-accent-tint text-accent',
};

export interface StatusBadgeProps {
  tone: BadgeTone;
  children: ReactNode;
  /** A slow blink beside the word, for a conversation happening right now. */
  pulse?: boolean;
  /** The size of the text; `xs` (12px) when absent. */
  size?: BadgeSize;
  /**
   * What the bubble says, for a pill that abbreviates a state (#103).
   *
   * The PROP keeps the name its callers already write; the sentence is drawn by
   * the package's `Tooltip` instead of being handed to the browser as a native
   * `title`.
   */
  title?: string;
  /**
   * The mark a suite reads this pill by, when its screen has one (#84).
   *
   * Optional and forwarded rather than invented per caller: the interview
   * screens find their badge through the row around it, and Réglages > Jetons
   * marks it directly - `jetons-jeton-etat`. It sits on the envelope, since the
   * package's `Badge` takes no `data-testid` of its own.
   */
  'data-testid'?: string;
}

/** The badge of the mockup: a pill, a tone, and nothing else. */
export function StatusBadge({
  tone,
  children,
  pulse,
  size = 'xs',
  title,
  'data-testid': testId,
}: StatusBadgeProps) {
  if (size !== 'xs') {
    const sized = (
      <span
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 font-semibold ${SIZE_CLASS[size]} ${TONE_CLASS[tone]}`}
      >
        {pulse === true ? (
          // `aria-hidden`: the word beside it already says « en cours ».
          <span aria-hidden="true" className="size-1.5 animate-pulse rounded-full bg-current" />
        ) : null}
        {children}
      </span>
    );
    return title === undefined ? sized : <Tooltip content={title}>{sized}</Tooltip>;
  }
  const badge = (
    <span data-testid={testId} className="inline-flex whitespace-nowrap">
      {/* `none`: a state is a word or two, and is never cut short. */}
      <Badge tone={tone} maxWidth="none">
        {pulse === true ? (
          <span className="inline-flex items-center gap-1">
            {/* `aria-hidden`: the word beside it already says « en cours », and a
                second announcement of the same fact is noise in a screen reader. */}
            <span aria-hidden="true" className="size-1.5 animate-pulse rounded-full bg-current" />
            {children}
          </span>
        ) : (
          children
        )}
      </Badge>
    </span>
  );
  // A pill without a `title` has nothing to say in a bubble.
  return title === undefined ? badge : <Tooltip content={title}>{badge}</Tooltip>;
}
